import { useState, useEffect } from 'react';
import { Users, Package, Truck, DollarSign, Leaf, Settings, CheckCircle, XCircle, Loader, Plus, Trash2, RefreshCw, AlertCircle, Download, MapPinned, Clock, Search, Filter, Sparkles, TrendingUp, Zap, ArrowRight, Calendar } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { logger } from '../utils/logger';
import jsPDF from 'jspdf';
import BackloadSection from '../components/BackloadSection';
import ErrorBoundary from '../components/ErrorBoundary';

interface ServiceArea {
  id?: string;
  postcode: string;
  address?: string; // Full address for display
  lat?: number;
  lon?: number;
  date: string;
  startTime: string;
  endTime: string;
  type: 'pickup' | 'delivery' | 'both';
}

interface MarketplaceOffer {
  id: string;
  vehicleVin: string;
  vehicleName?: string;
  capacityKg?: number;
  capacityM3?: number;
  availableKg: number;
  availableM3?: number;
  route?: Array<{ lat: number; lon: number; address?: string }>;
  serviceAreas?: ServiceArea[]; // Multiple service areas with dates/times
  pickupArea?: {
    address?: string;
    postcode?: string;
    lat?: number;
    lon?: number;
  };
  deliveryArea?: {
    address?: string;
    postcode?: string;
    lat?: number;
    lon?: number;
  };
  pickupDate?: string;
  pickupTime?: string;
  deliveryDate?: string;
  deliveryTime?: string;
  pricePerKg: number;
  pricePerKm: number;
  terms?: string;
  operatorName?: string;
  operatorEmail?: string;
  status: string;
  createdAt: string;
}

interface MarketplaceNeed {
  id: string;
  orderId: string;
  orderName?: string;
  weightKg: number;
  volumeM3?: number;
  pickup: {
    address?: string;
    postcode?: string;
    lat?: number;
    lon?: number;
    date?: string;
    time?: string;
  };
  delivery: {
    address?: string;
    postcode?: string;
    lat?: number;
    lon?: number;
    date?: string;
    time?: string;
  };
  willingToPay?: number;
  maxPrice?: number;
  deliveryDate?: string;
  operatorName?: string;
  status: string;
  createdAt: string;
}

interface MarketplaceMatch {
  id: string;
  offerId: string;
  needId: string;
  score: number;
  price: number;
  pickupDistance?: number;
  deliveryDistance?: number;
  carbonSavings: number;
  baselineEmissions: number;
  offerEmissions: number;
  offer?: MarketplaceOffer;
  need?: MarketplaceNeed;
  status: string;
  createdAt: string;
  agreement?: {
    palletDetails?: {
      weightKg?: number;
      volumeM3?: number;
      description?: string;
    };
    pickupLocation?: {
      address?: string;
      postcode?: string;
      date?: string;
      time?: string;
    };
    deliveryLocation?: {
      address?: string;
      postcode?: string;
      date?: string;
      time?: string;
    };
  };
}

interface MarketplaceConfig {
  pricePerKg: number;
  pricePerKm: number;
  minOrderValue: number;
  maxDistanceKm: number;
  matchingRadiusKm: number; // Radius for automatic matching
  slaHours: number;
  autoMatchEnabled: boolean;
  terms: string;
}

const Collaborate: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'backload' | 'marketplace' | 'offers' | 'needs' | 'matches' | 'config'>('backload');
  const [offers, setOffers] = useState<MarketplaceOffer[]>([]);
  const [needs, setNeeds] = useState<MarketplaceNeed[]>([]);
  const [matches, setMatches] = useState<MarketplaceMatch[]>([]);
  const [config, setConfig] = useState<MarketplaceConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planningResult, setPlanningResult] = useState<any>(null);
  const [fleetVehicles, setFleetVehicles] = useState<any[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  // Form states
  const [showCreateOffer, setShowCreateOffer] = useState(false);
  const [showCreateNeed, setShowCreateNeed] = useState(false);
  const [newOffer, setNewOffer] = useState<Partial<MarketplaceOffer>>({});
  const [newNeed, setNewNeed] = useState<Partial<MarketplaceNeed>>({});
  const [configForm, setConfigForm] = useState<Partial<MarketplaceConfig>>({});
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [selectedOfferForMatching, setSelectedOfferForMatching] = useState<MarketplaceOffer | null>(null);
  const [manualMatchDetails, setManualMatchDetails] = useState<{
    weightKg?: number;
    volumeM3?: number;
    description?: string;
    pickupPostcode?: string;
    pickupAddress?: string;
    pickupDate?: string;
    pickupTime?: string;
    deliveryPostcode?: string;
    deliveryAddress?: string;
    deliveryDate?: string;
    deliveryTime?: string;
  }>({});
  const [autoGeneratedOrderId, setAutoGeneratedOrderId] = useState<string>('');
  
  // Marketplace filters and search
  const [marketplaceSearch, setMarketplaceSearch] = useState<string>('');
  const [marketplaceFilter, setMarketplaceFilter] = useState<'all' | 'offers' | 'needs'>('all');

  // Address autocomplete state (per service area and pickup/delivery)
  const [addressSuggestions, setAddressSuggestions] = useState<{ [key: string]: Array<{address: string, postcode: string}> }>({});
  const [showSuggestions, setShowSuggestions] = useState<{ [key: string]: boolean }>({});

  // Geocode a postcode (helper function) - uses backend geocoding API
  const geocodePostcode = async (postcode: string): Promise<{ lat: number; lon: number } | null> => {
    if (!postcode || !postcode.trim()) return null;
    
    try {
      const url = API_ENDPOINTS.geocode(postcode.trim(), 'GB');
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.lat != null && data.lon != null) {
          return { lat: parseFloat(String(data.lat)), lon: parseFloat(String(data.lon)) };
        }
      } else if (response.status === 404) {
        // Geocoding failed - not found
        return null;
      }
      return null;
    } catch (err) {
      logger.error('Error geocoding postcode:', err);
      return null;
    }
  };

  // Search for addresses (UK postcode lookup with address suggestions) - uses backend autocomplete API
  const searchAddresses = async (query: string, areaId: string) => {
    if (!query || query.length < 2) {
      setAddressSuggestions(prev => ({ ...prev, [areaId]: [] }));
      setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
      return;
    }

    try {
      const url = API_ENDPOINTS.geocodeAutocomplete(query, 'GB', 5);
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.suggestions && data.suggestions.length > 0) {
          const suggestions = data.suggestions.map((item: any) => ({
            address: item.address || item.postcode || query,
            postcode: item.postcode || ''
          }));
          setAddressSuggestions(prev => ({ ...prev, [areaId]: suggestions }));
          setShowSuggestions(prev => ({ ...prev, [areaId]: true }));
        } else {
          setAddressSuggestions(prev => ({ ...prev, [areaId]: [] }));
          setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
        }
      } else {
        setAddressSuggestions(prev => ({ ...prev, [areaId]: [] }));
        setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
      }
    } catch (err) {
      logger.error('Error searching addresses:', err);
      setAddressSuggestions(prev => ({ ...prev, [areaId]: [] }));
      setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
    }
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Load all data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, needsRes, matchesRes, configRes] = await Promise.all([
        fetch(API_ENDPOINTS.marketplaceOffers()),
        fetch(API_ENDPOINTS.marketplaceNeeds()),
        fetch(API_ENDPOINTS.marketplaceMatches()),
        fetch(API_ENDPOINTS.marketplaceConfig())
      ]);

      const offersData = await offersRes.json();
      const needsData = await needsRes.json();
      const matchesData = await matchesRes.json();
      const configData = await configRes.json();

      if (offersData.ok) setOffers(offersData.offers || []);
      if (needsData.ok) setNeeds(needsData.needs || []);
      if (matchesData.ok) setMatches(matchesData.matches || []);
      if (configData.ok) {
        setConfig(configData.config);
        setConfigForm(configData.config);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load partner network data';
      setError(message);
      logger.error('Error loading partner network data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load planning result to create offers from
  const loadPlanningResult = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.planning());
      const data = await response.json();
      if (data.ok && data.vehicles) {
        setPlanningResult(data);
      }
    } catch (err) {
      logger.error('Error loading planning result:', err);
    }
  };

  // Load fleet vehicles
  const loadFleetVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const response = await fetch(API_ENDPOINTS.fleetVehicles());
      const data = await response.json();
      if (data.ok && data.vehicles) {
        setFleetVehicles(data.vehicles);
      }
    } catch (err) {
      logger.error('Error loading fleet vehicles:', err);
    } finally {
      setLoadingVehicles(false);
    }
  };

  // Estimate capacity based on vehicle type/model (matching backend logic)
  const estimateVehicleCapacity = (vehicle: any) => {
    const type = (vehicle.type || '').toUpperCase();
    const model = (vehicle.model || '').toUpperCase();

    // Model-based overrides
    if (model.includes('T BEV')) {
      return { weightKg: 18000, volumeM3: 50 };
    }
    if (model.includes('T HIGH')) {
      return { weightKg: 24000, volumeM3: 60 };
    }
    if (model.includes('D WIDE') || model.includes('D16') || model.includes('D18')) {
      return { weightKg: 16000, volumeM3: 45 };
    }

    // Fallback by type
    if (type === 'TRUCK' || type === 'RIGID') {
      return { weightKg: 16000, volumeM3: 45 };
    }
    if (type === 'TRACTOR') {
      return { weightKg: 24000, volumeM3: 60 };
    }
    if (type === 'VAN' || type === 'LCV') {
      return { weightKg: 3500, volumeM3: 18 };
    }

    // Very conservative default
    return { weightKg: 12000, volumeM3: 35 };
  };

  // Handle vehicle selection from dropdown
  const handleVehicleSelect = (vin: string) => {
    const vehicle = fleetVehicles.find(v => v.vin === vin);
    if (vehicle) {
      const capacity = estimateVehicleCapacity(vehicle);
      
      setNewOffer({
        ...newOffer,
        vehicleVin: vehicle.vin,
        vehicleName: vehicle.customerVehicleName || vehicle.vin,
        capacityKg: capacity.weightKg,
        capacityM3: capacity.volumeM3,
        availableKg: capacity.weightKg, // Start with full capacity, user can adjust
        availableM3: capacity.volumeM3
      });
    }
  };

  useEffect(() => {
    loadData();
    loadPlanningResult();
    loadFleetVehicles();
  }, []);

  // Create offer from planning result
  const createOfferFromPlanning = async (vehicle: any) => {
    const assignedWeight = (vehicle.assignedOrders || []).reduce((sum: number, o: any) => sum + (o.weightKg || 0), 0);
    const assignedVolume = (vehicle.assignedOrders || []).reduce((sum: number, o: any) => sum + (o.volumeM3 || 0), 0);
    const availableKg = Math.max(0, (vehicle.capacity_kg || 0) - assignedWeight);
    const availableM3 = Math.max(0, (vehicle.capacity_m3 || 0) - assignedVolume);

    if (availableKg <= 0) {
      setError('Vehicle has no available capacity');
      return;
    }

    const route = (vehicle.assignedOrders || []).map((o: any) => ({
      lat: o.delivery?.lat,
      lon: o.delivery?.lon,
      address: o.delivery?.address
    })).filter((r: any) => r.lat && r.lon);

    const offer: Partial<MarketplaceOffer> = {
      vehicleVin: vehicle.vin,
      vehicleName: vehicle.customerVehicleName || vehicle.vin,
      capacityKg: vehicle.capacity_kg,
      capacityM3: vehicle.capacity_m3,
      availableKg,
      availableM3,
      route,
      pricePerKg: config?.pricePerKg || 0.50,
      pricePerKm: config?.pricePerKm || 0.30,
      terms: config?.terms || ''
    };

    try {
      const response = await fetch(API_ENDPOINTS.marketplaceOffers(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offer)
      });

      const data = await response.json();
      if (data.ok) {
        await loadData();
        setShowCreateOffer(false);
      } else {
        throw new Error(data.error || 'Failed to create offer');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create offer';
      setError(message);
    }
  };

  // Create offer manually
  const createOffer = async () => {
    if (!newOffer.vehicleVin || !newOffer.availableKg) {
      setError('Vehicle VIN and available capacity are required');
      return;
    }

    if (serviceAreas.length === 0) {
      setError('Please add at least one service area with postcode, date, and time range');
      return;
    }

    // Validate service areas
    const invalidAreas = serviceAreas.filter(area => (!area.postcode && !area.address) || !area.date || !area.startTime || !area.endTime);
    if (invalidAreas.length > 0) {
      setError('All service areas must have a location (address or postcode), date, start time, and end time');
      return;
    }
    
    // Ensure postcode is set (extract from address if needed)
    serviceAreas.forEach(area => {
      if (!area.postcode && area.address) {
        // Try to extract postcode from address (UK postcode pattern)
        const postcodeMatch = area.address.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})\b/i);
        if (postcodeMatch) {
          area.postcode = postcodeMatch[1].toUpperCase().replace(/\s+/g, ' ');
        }
      }
    });

    const offerData = {
      ...newOffer,
      serviceAreas: serviceAreas.map(area => ({
        postcode: area.postcode,
        date: area.date,
        startTime: area.startTime,
        endTime: area.endTime,
        type: area.type
      })),
      // Keep legacy fields for backward compatibility
      pickupArea: undefined,
      deliveryArea: undefined,
      route: undefined,
    };

    try {
      const response = await fetch(API_ENDPOINTS.marketplaceOffers(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offerData)
      });

      const data = await response.json();
      if (data.ok) {
        await loadData();
        setShowCreateOffer(false);
        setNewOffer({});
        setServiceAreas([]);
        setError(null);
      } else {
        throw new Error(data.error || 'Failed to create offer');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create offer';
      setError(message);
    }
  };

  // Generate order ID
  const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    return `NEED-${timestamp}-${random}`;
  };

  // Generate order ID when showing create need form
  useEffect(() => {
    if (showCreateNeed && !newNeed.orderId) {
      const generatedId = generateOrderId();
      setAutoGeneratedOrderId(generatedId);
      setNewNeed(prev => ({ ...prev, orderId: generatedId }));
    } else if (!showCreateNeed) {
      setAutoGeneratedOrderId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateNeed]);

  // Create need
  const createNeed = async () => {
    if (!newNeed.weightKg) {
      setError('Weight is required');
      return;
    }

    if ((!newNeed.pickup?.postcode && !newNeed.pickup?.address) || (!newNeed.delivery?.postcode && !newNeed.delivery?.address)) {
      setError('Pickup and delivery locations (address or postcode) are required for matching');
      return;
    }
    
    // Ensure postcodes are set (extract from address if needed)
    if (!newNeed.pickup?.postcode && newNeed.pickup?.address) {
      const postcodeMatch = newNeed.pickup.address.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})\b/i);
      if (postcodeMatch) {
        newNeed.pickup.postcode = postcodeMatch[1].toUpperCase().replace(/\s+/g, ' ');
      }
    }
    if (!newNeed.delivery?.postcode && newNeed.delivery?.address) {
      const postcodeMatch = newNeed.delivery.address.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})\b/i);
      if (postcodeMatch) {
        newNeed.delivery.postcode = postcodeMatch[1].toUpperCase().replace(/\s+/g, ' ');
      }
    }
    
    if (!newNeed.pickup?.postcode || !newNeed.delivery?.postcode) {
      setError('Could not extract postcode from addresses. Please ensure addresses include valid UK postcodes.');
      return;
    }

    // Auto-generate order ID if not provided
    const orderId = newNeed.orderId || generateOrderId();

    // Ensure pickup and delivery objects exist
    const needData = {
      ...newNeed,
      orderId: orderId,
      pickup: {
        address: newNeed.pickup?.address || '',
        postcode: newNeed.pickup?.postcode || '',
        date: newNeed.pickup?.date || '',
        time: newNeed.pickup?.time || '',
        ...newNeed.pickup
      },
      delivery: {
        address: newNeed.delivery?.address || '',
        postcode: newNeed.delivery?.postcode || '',
        date: newNeed.delivery?.date || newNeed.deliveryDate || '',
        time: newNeed.delivery?.time || '',
        ...newNeed.delivery
      }
    };

    try {
      const response = await fetch(API_ENDPOINTS.marketplaceNeeds(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(needData)
      });

      const data = await response.json();
      if (data.ok) {
        await loadData();
        setShowCreateNeed(false);
        setNewNeed({});
        setAutoGeneratedOrderId('');
        setError(null);
      } else {
        throw new Error(data.error || 'Failed to create need');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create need';
      setError(message);
    }
  };

  // Run matching - backend handles geocoding
  const runMatching = async () => {
    setLoading(true);
    setError(null);
    try {
      // Backend will handle geocoding and matching
      const response = await fetch(API_ENDPOINTS.marketplaceMatch(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          radiusKm: config?.matchingRadiusKm || 5
        })
      });

      const data = await response.json();
      if (data.ok) {
        await loadData();
        if (data.autoMatched > 0) {
          setActiveSection('matches');
        }
        if (data.count > 0) {
          setActiveSection('matches');
        }
      } else {
        throw new Error(data.error || 'Matching failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to run matching';
      setError(message);
      logger.error('Matching error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Accept match
  const acceptMatch = async (matchId: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.marketplaceMatches()}/${matchId}/accept`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.ok) {
        await loadData();
      } else {
        throw new Error(data.error || 'Failed to accept match');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept match';
      setError(message);
    }
  };

  // Manual matching: Take an offer and create a match
  const takeOffer = async () => {
    if (!selectedOfferForMatching) {
      setError('No offer selected');
      return;
    }

    // Validate offer has ID
    if (!selectedOfferForMatching.id) {
      setError('Offer ID is missing. Please refresh the page and try again.');
      logger.error('Offer missing ID', { offer: selectedOfferForMatching });
      return;
    }

    if (!manualMatchDetails.weightKg || !manualMatchDetails.pickupPostcode || !manualMatchDetails.deliveryPostcode) {
      setError('Weight, pickup postcode, and delivery postcode are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Geocode locations
      const pickupCoords = await geocodePostcode(manualMatchDetails.pickupPostcode);
      const deliveryCoords = await geocodePostcode(manualMatchDetails.deliveryPostcode);

      if (!pickupCoords || !deliveryCoords) {
        setError('Could not geocode postcodes. Please check they are valid UK postcodes.');
        setLoading(false);
        return;
      }

      // Geocode service areas if needed and find matching service area
      let matchingServiceArea = null;
      const radius = config?.matchingRadiusKm || 5;
      
      if (selectedOfferForMatching.serviceAreas && selectedOfferForMatching.serviceAreas.length > 0) {
        // Geocode service areas that don't have coordinates
        for (const area of selectedOfferForMatching.serviceAreas) {
          if (!area.lat || !area.lon) {
            if (area.postcode) {
              const coords = await geocodePostcode(area.postcode);
              if (coords) {
                area.lat = coords.lat;
                area.lon = coords.lon;
              }
            }
          }
          
          // Check if this area matches delivery location
          if (area.lat && area.lon) {
            if (area.type === 'delivery' || area.type === 'both') {
              const distance = calculateDistance(area.lat, area.lon, deliveryCoords.lat, deliveryCoords.lon);
              if (distance <= radius) {
                if (!matchingServiceArea || distance < calculateDistance(
                  matchingServiceArea.lat || 0,
                  matchingServiceArea.lon || 0,
                  deliveryCoords.lat,
                  deliveryCoords.lon
                )) {
                  matchingServiceArea = area;
                }
              }
            }
          }
        }
      }

      // Calculate distances
      const distance = calculateDistance(pickupCoords.lat, pickupCoords.lon, deliveryCoords.lat, deliveryCoords.lon);
      const pickupDistance = matchingServiceArea && matchingServiceArea.lat && matchingServiceArea.lon
        ? calculateDistance(pickupCoords.lat, pickupCoords.lon, matchingServiceArea.lat, matchingServiceArea.lon)
        : distance;
      const deliveryDistance = matchingServiceArea && matchingServiceArea.lat && matchingServiceArea.lon
        ? calculateDistance(matchingServiceArea.lat, matchingServiceArea.lon, deliveryCoords.lat, deliveryCoords.lon)
        : distance;

      // Calculate price first
      const calculatedPrice = (manualMatchDetails.weightKg * (selectedOfferForMatching.pricePerKg || 0)) + 
                              (pickupDistance * (selectedOfferForMatching.pricePerKm || 0)) +
                              (deliveryDistance * (selectedOfferForMatching.pricePerKm || 0));

      // Create a need from the manual match details
      const needData = {
        orderId: `MANUAL-${Date.now()}`,
        orderName: manualMatchDetails.description || 'Manual Match',
        weightKg: manualMatchDetails.weightKg,
        volumeM3: manualMatchDetails.volumeM3,
        pickup: {
          postcode: manualMatchDetails.pickupPostcode,
          address: manualMatchDetails.pickupAddress,
          lat: pickupCoords.lat,
          lon: pickupCoords.lon,
          date: manualMatchDetails.pickupDate,
          time: manualMatchDetails.pickupTime
        },
        delivery: {
          postcode: manualMatchDetails.deliveryPostcode,
          address: manualMatchDetails.deliveryAddress,
          lat: deliveryCoords.lat,
          lon: deliveryCoords.lon,
          date: manualMatchDetails.deliveryDate,
          time: manualMatchDetails.deliveryTime
        },
        maxPrice: calculatedPrice * 1.1, // Allow 10% buffer
        operatorName: 'Manual Match User'
      };
      // Create the need
      const needResponse = await fetch(API_ENDPOINTS.marketplaceNeeds(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(needData)
      });

      const needResult = await needResponse.json();
      if (!needResult.ok) {
        logger.error('Need creation failed', { needResult });
        throw new Error(needResult.error || 'Failed to create need');
      }

      // Validate IDs exist
      if (!selectedOfferForMatching.id) {
        logger.error('Offer missing ID', { 
          offer: selectedOfferForMatching,
          offerKeys: Object.keys(selectedOfferForMatching)
        });
        throw new Error('Offer ID is missing. Please refresh and try again.');
      }

      if (!needResult.need || !needResult.need.id) {
        logger.error('Need creation response missing ID', { 
          needResult,
          hasNeed: !!needResult.need,
          needKeys: needResult.need ? Object.keys(needResult.need) : []
        });
        throw new Error('Failed to get need ID from server response. Server returned: ' + JSON.stringify(needResult));
      }

      // Double-check IDs before creating match
      const finalOfferId = selectedOfferForMatching.id;
      const finalNeedId = needResult.need.id;

      if (!finalOfferId || !finalNeedId) {
        logger.error('Missing IDs before match creation', {
          offerId: finalOfferId,
          needId: finalNeedId,
          offer: selectedOfferForMatching,
          need: needResult.need,
          offerKeys: Object.keys(selectedOfferForMatching),
          needKeys: Object.keys(needResult.need)
        });
        throw new Error(`Missing required IDs: offerId=${finalOfferId || 'undefined'}, needId=${finalNeedId || 'undefined'}`);
      }

      // Create match with agreement details
      const matchData = {
        offerId: finalOfferId,
        needId: finalNeedId,
        price: calculatedPrice,
        pickupDistance: pickupDistance,
        deliveryDistance: deliveryDistance,
        agreement: {
          palletDetails: {
            weightKg: manualMatchDetails.weightKg,
            volumeM3: manualMatchDetails.volumeM3,
            description: manualMatchDetails.description
          },
          pickupLocation: {
            postcode: manualMatchDetails.pickupPostcode,
            address: manualMatchDetails.pickupAddress,
            date: manualMatchDetails.pickupDate,
            time: manualMatchDetails.pickupTime
          },
          deliveryLocation: {
            postcode: manualMatchDetails.deliveryPostcode,
            address: manualMatchDetails.deliveryAddress,
            date: manualMatchDetails.deliveryDate,
            time: manualMatchDetails.deliveryTime
          }
        },
        status: 'accepted'
      };

      logger.info('Creating match', { 
        offerId: matchData.offerId, 
        needId: matchData.needId,
        hasOfferId: !!matchData.offerId,
        hasNeedId: !!matchData.needId,
        matchDataKeys: Object.keys(matchData)
      });

      // Create match
      const matchResponse = await fetch(API_ENDPOINTS.marketplaceMatches(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(matchData)
      });

      if (!matchResponse.ok) {
        const errorText = await matchResponse.text();
        logger.error('Match creation HTTP error', { 
          status: matchResponse.status,
          statusText: matchResponse.statusText,
          errorText,
          matchData
        });
        throw new Error(`Failed to create match: ${matchResponse.status} ${matchResponse.statusText}`);
      }

      const matchResult = await matchResponse.json();
      
      if (!matchResult.ok) {
        logger.error('Match creation failed', { 
          matchResult, 
          matchData,
          status: matchResponse.status
        });
        throw new Error(matchResult.error || 'Failed to create match');
      }

      if (matchResult.ok) {
        // Generate and download receipt
        generateAndDownloadAgreement(matchResult.match, selectedOfferForMatching, needResult.need);
        
        // Send to inbox (we'll implement this)
        await sendAgreementToInbox(selectedOfferForMatching.operatorEmail, matchResult.match);
        
        setSelectedOfferForMatching(null);
        setManualMatchDetails({});
        await loadData();
        setActiveSection('matches');
      } else {
        throw new Error(matchResult.error || 'Failed to create match');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to take offer';
      setError(message);
      logger.error('Error taking offer:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate and download agreement/receipt as PDF
  const generateAndDownloadAgreement = (match: MarketplaceMatch, offer: MarketplaceOffer, need: MarketplaceNeed) => {
    const agreement = {
      type: 'Partner Network Agreement',
      matchId: match.id,
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0],
      status: match.status,

      offer: {
        vehicleVin: offer.vehicleVin,
        vehicleName: offer.vehicleName || offer.vehicleVin,
        operator: offer.operatorName || 'Operator A',
        email: offer.operatorEmail || 'N/A'
      },
      
      need: {
        orderId: need.orderId,
        orderName: need.orderName || need.orderId,
        operator: need.operatorName || 'Operator B'
      },
      
      palletDetails: match.agreement?.palletDetails || {
        weightKg: need.weightKg,
        volumeM3: need.volumeM3,
        description: 'N/A'
      },
      
      locations: {
        pickup: match.agreement?.pickupLocation || {
          postcode: need.pickup.postcode,
          address: need.pickup.address,
          date: need.pickup.date,
          time: need.pickup.time
        },
        delivery: match.agreement?.deliveryLocation || {
          postcode: need.delivery.postcode,
          address: need.delivery.address,
          date: need.delivery.date,
          time: need.delivery.time
        }
      },
      
      pricing: {
        pricePerKg: offer.pricePerKg,
        pricePerKm: offer.pricePerKm,
        totalPrice: match.price,
        pickupDistance: match.pickupDistance,
        deliveryDistance: match.deliveryDistance
      },
      
      terms: offer.terms || config?.terms || 'Standard partner network terms apply.'
    };

    generatePDFReceipt(agreement, match, offer, need);
  };

  // Helper function to generate PDF receipt
  const generatePDFReceipt = (
    agreement: {
      type: string;
      matchId: string;
      date: string;
      time: string;
      status: string;
      offer: any;
      need: any;
      palletDetails?: any;
      locations?: any;
      pricing: any;
      carbonImpact?: any;
      terms: string;
    },
    match: MarketplaceMatch,
    offer: MarketplaceOffer,
    need: MarketplaceNeed
  ) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPos = 20;

    // Helper to draw a horizontal line
    const drawLine = (y: number, width: number = pageWidth - (margin * 2)) => {
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(margin, y, margin + width, y);
    };

    // Helper to add text with word wrap
    const addText = (text: string, fontSize: number = 10, isBold: boolean = false, color: string = '#000000', x: number = margin, align: 'left' | 'center' | 'right' = 'left') => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(fontSize);
      doc.setTextColor(color);
      if (isBold) {
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setFont('helvetica', 'normal');
      }
      
      const lines = doc.splitTextToSize(text, maxWidth);
      const textX = align === 'center' ? pageWidth / 2 : align === 'right' ? pageWidth - margin : x;
      doc.text(lines, textX, yPos, { align });
      yPos += lines.length * (fontSize * 0.4) + 5;
    };

    // Helper to add a section header
    const addSectionHeader = (title: string) => {
      yPos += 5;
      drawLine(yPos);
      yPos += 8;
      addText(title, 11, true, '#333333');
      yPos += 2;
    };

    // Header with company info
    doc.setFillColor(30, 58, 138); // Dark blue
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZECURE PARTNER NETWORK', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Collaborative Logistics Receipt', pageWidth / 2, 30, { align: 'center' });
    
    yPos = 55;

    // Receipt Number and Date
    doc.setTextColor(0, 0, 0);
    addText('RECEIPT', 14, true, '#1e3a8a', margin);
    yPos -= 8;
    addText(`Receipt #: ${match.id}`, 10, false, '#666666', pageWidth - margin, 'right');
    addText(`Date: ${new Date(agreement.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`, 10, false, '#666666', pageWidth - margin, 'right');
    addText(`Time: ${agreement.time}`, 10, false, '#666666', pageWidth - margin, 'right');
    addText(`Status: ${match.status.toUpperCase()}`, 10, true, match.status === 'accepted' ? '#10b981' : '#666666', pageWidth - margin, 'right');
    
    yPos += 5;
    drawLine(yPos);
    yPos += 10;

    // Two-column layout helper
    const addTwoColumn = (left: string, right: string, leftBold: boolean = false, rightBold: boolean = false) => {
      const currentY = yPos;
      doc.setFontSize(9);
      doc.setFont('helvetica', leftBold ? 'bold' : 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(left, margin, currentY);
      doc.setFont('helvetica', rightBold ? 'bold' : 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(right, pageWidth - margin, currentY, { align: 'right' });
      yPos += 6;
    };

    // Service Provider (Offer) Section
    addSectionHeader('SERVICE PROVIDER');
    addTwoColumn('Vehicle VIN:', offer.vehicleVin, false, true);
    addTwoColumn('Vehicle Name:', offer.vehicleName || offer.vehicleVin);
    addTwoColumn('Operator:', offer.operatorName || 'Operator A');
    if (offer.operatorEmail) {
      addTwoColumn('Email:', offer.operatorEmail);
    }

    // Customer (Need) Section
    addSectionHeader('CUSTOMER');
    addTwoColumn('Order ID:', need.orderId, false, true);
    addTwoColumn('Order Name:', need.orderName || need.orderId);
    addTwoColumn('Operator:', need.operatorName || 'Operator B');

    // Shipment Details
    addSectionHeader('SHIPMENT DETAILS');
    addTwoColumn('Weight:', `${agreement.palletDetails.weightKg?.toFixed(0) || 'N/A'} kg`, false, true);
    if (agreement.palletDetails.volumeM3) {
      addTwoColumn('Volume:', `${agreement.palletDetails.volumeM3.toFixed(2)} m³`);
    }
    if (agreement.palletDetails.description && agreement.palletDetails.description !== 'N/A') {
      yPos += 2;
      addText(`Description: ${agreement.palletDetails.description}`, 9, false, '#666666');
      yPos += 2;
    }

    // Pickup Information
    addSectionHeader('PICKUP INFORMATION');
    if (agreement.locations.pickup.address) {
      addText(agreement.locations.pickup.address, 9, false, '#000000');
      yPos -= 2;
    }
    addTwoColumn('Postcode:', agreement.locations.pickup.postcode || 'N/A');
    addTwoColumn('Date:', agreement.locations.pickup.date || 'N/A');
    if (agreement.locations.pickup.time) {
      addTwoColumn('Time:', agreement.locations.pickup.time);
    }

    // Delivery Information
    addSectionHeader('DELIVERY INFORMATION');
    if (agreement.locations.delivery.address) {
      addText(agreement.locations.delivery.address, 9, false, '#000000');
      yPos -= 2;
    }
    addTwoColumn('Postcode:', agreement.locations.delivery.postcode || 'N/A');
    addTwoColumn('Date:', agreement.locations.delivery.date || 'N/A');
    if (agreement.locations.delivery.time) {
      addTwoColumn('Time:', agreement.locations.delivery.time);
    }

    // Pricing Breakdown
    addSectionHeader('PRICING BREAKDOWN');
    yPos += 3;
    
    // Calculate individual charges
    const weightCharge = (agreement.palletDetails.weightKg || 0) * (agreement.pricing.pricePerKg || 0);
    const pickupCharge = (agreement.pricing.pickupDistance || 0) * (agreement.pricing.pricePerKm || 0);
    const deliveryCharge = (agreement.pricing.deliveryDistance || 0) * (agreement.pricing.pricePerKm || 0);
    
    // Table-like pricing display
    const pricingItems = [
      { 
        label: 'Weight Charge', 
        quantity: `${agreement.palletDetails.weightKg?.toFixed(0) || 0} kg`, 
        rate: `£${agreement.pricing.pricePerKg.toFixed(2)}/kg`,
        amount: weightCharge
      },
      { 
        label: 'Pickup Distance', 
        quantity: agreement.pricing.pickupDistance ? `${agreement.pricing.pickupDistance.toFixed(2)} km` : 'N/A', 
        rate: `£${agreement.pricing.pricePerKm.toFixed(2)}/km`,
        amount: pickupCharge
      },
      { 
        label: 'Delivery Distance', 
        quantity: agreement.pricing.deliveryDistance ? `${agreement.pricing.deliveryDistance.toFixed(2)} km` : 'N/A', 
        rate: `£${agreement.pricing.pricePerKm.toFixed(2)}/km`,
        amount: deliveryCharge
      },
    ];

    // Draw table header
    const tableY = yPos;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Description', margin, tableY);
    doc.text('Quantity', margin + 70, tableY);
    doc.text('Rate', margin + 120, tableY);
    doc.text('Amount', pageWidth - margin, tableY, { align: 'right' });
    yPos += 6;
    drawLine(yPos - 2);

    // Draw table rows
    pricingItems.forEach(item => {
      const currentY = yPos;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(item.label, margin, currentY);
      doc.setTextColor(100, 100, 100);
      doc.text(item.quantity, margin + 70, currentY);
      doc.text(item.rate, margin + 120, currentY);
      doc.setTextColor(0, 0, 0);
      doc.text(`£${item.amount.toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      yPos += 6;
    });

    yPos += 3;
    drawLine(yPos);
    yPos += 5;
    
    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('TOTAL AMOUNT', margin, yPos);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`£${agreement.pricing.totalPrice.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Carbon Impact (if available)
    if (match.carbonSavings !== undefined && match.carbonSavings > 0) {
      addSectionHeader('ENVIRONMENTAL IMPACT');
      doc.setFillColor(240, 253, 244); // Light green background
      doc.roundedRect(margin, yPos - 5, maxWidth, 25, 3, 3, 'F');
      
      const currentY = yPos;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Carbon Savings:', margin + 5, currentY);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129); // Green
      doc.text(`${match.carbonSavings.toFixed(3)} kg CO₂`, margin + 5, currentY + 7);
      
      const reductionPercent = match.baselineEmissions > 0 
        ? ((match.carbonSavings / match.baselineEmissions) * 100).toFixed(1)
        : '0';
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Reduction: ${reductionPercent}%`, pageWidth - margin - 5, currentY + 7, { align: 'right' });
      
      yPos += 25;
    }

    // Footer
    yPos = pageHeight - 30;
    drawLine(yPos);
    yPos += 8;
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('This is an automated receipt generated by ZECURE Partner Network.', pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.text(`Generated on ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    
    if (agreement.terms && agreement.terms !== 'Standard partner network terms apply.') {
      doc.setFontSize(6);
      const termsLines = doc.splitTextToSize(`Terms: ${agreement.terms}`, maxWidth - 10);
      doc.text(termsLines, pageWidth / 2, yPos, { align: 'center', maxWidth: maxWidth - 10 });
    }

    // Save PDF
    const filename = agreement.type === 'Partner Network Match Receipt' 
      ? `receipt-${match.id}-${agreement.date.replace(/-/g, '')}.pdf`
      : `agreement-${match.id}-${agreement.date.replace(/-/g, '')}.pdf`;
    doc.save(filename);
  };

  // Send agreement to inbox (placeholder - would integrate with email/notification system)
  const sendAgreementToInbox = async (email: string | undefined, match: MarketplaceMatch) => {
    if (!email) {
      logger.warn('No email provided for offer owner, cannot send agreement');
      return;
    }
    
    // In a real implementation, this would call a backend endpoint to send email/notification
    logger.info('Agreement sent to inbox', { email, matchId: match.id });
    // For now, we'll just log it - in production, this would integrate with your notification system
  };

  // Download receipt for a match as PDF
  const downloadReceipt = (match: MarketplaceMatch) => {
    const offer = match.offer || offers.find(o => o.id === match.offerId);
    const need = match.need || needs.find(n => n.id === match.needId);
    
    if (!offer || !need) {
      setError('Match details incomplete');
      return;
    }

    const receipt = {
      type: 'Partner Network Match Receipt',
      matchId: match.id,
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0],
      status: match.status,
      
      offer: {
        vehicleVin: offer.vehicleVin,
        vehicleName: offer.vehicleName || offer.vehicleVin,
        operator: offer.operatorName || 'Operator A',
        email: offer.operatorEmail || 'N/A'
      },
      
      need: {
        orderId: need.orderId,
        orderName: need.orderName || need.orderId,
        operator: need.operatorName || 'Operator B'
      },
      
      palletDetails: {
        weightKg: need.weightKg,
        volumeM3: need.volumeM3,
        description: 'N/A'
      },
      
      locations: {
        pickup: {
          postcode: need.pickup.postcode,
          address: need.pickup.address,
          date: need.pickup.date,
          time: need.pickup.time
        },
        delivery: {
          postcode: need.delivery.postcode,
          address: need.delivery.address,
          date: need.delivery.date,
          time: need.delivery.time
        }
      },
      
      pricing: {
        pricePerKg: offer.pricePerKg,
        pricePerKm: offer.pricePerKm,
        pickupDistance: match.pickupDistance,
        deliveryDistance: match.deliveryDistance,
        totalPrice: match.price
      },
      
      carbonImpact: {
        baselineEmissions: match.baselineEmissions,
        offerEmissions: match.offerEmissions,
        carbonSavings: match.carbonSavings,
        reductionPercent: match.baselineEmissions > 0 
          ? ((match.carbonSavings / match.baselineEmissions) * 100).toFixed(1)
          : '0'
      },
      
      terms: offer.terms || config?.terms || 'Standard partner network terms apply.'
    };

    // Use the same PDF generation function
    generatePDFReceipt(receipt, match, offer, need);
  };

  // Delete offer
  const deleteOffer = async (offerId: string) => {
    if (!confirm('Delete this offer?')) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.marketplaceOffers()}/${offerId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await loadData();
      }
    } catch (err) {
      logger.error('Error deleting offer:', err);
    }
  };

  // Delete need
  const deleteNeed = async (needId: string) => {
    if (!confirm('Delete this need?')) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.marketplaceNeeds()}/${needId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await loadData();
      }
    } catch (err) {
      logger.error('Error deleting need:', err);
    }
  };

  // Update config
  const updateConfig = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.marketplaceConfig(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configForm)
      });

      const data = await response.json();
      if (data.ok) {
        setConfig(data.config);
        setConfigForm(data.config);
      } else {
        throw new Error(data.error || 'Failed to update config');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update config';
      setError(message);
    }
  };

  const totalCarbonSavings = matches
    .filter(m => m.status === 'accepted' || m.status === 'auto-matched')
    .reduce((sum, m) => sum + (m.carbonSavings || 0), 0);

  const totalValue = matches
    .filter(m => m.status === 'accepted' || m.status === 'auto-matched')
    .reduce((sum, m) => sum + (m.price || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Users className="w-8 h-8" />
          Collaborate
        </h1>
        <p className="text-muted-foreground text-sm">
          Partner Network for sharing vehicle capacity. Operators can offer available space or post needs for capacity.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/20 border border-destructive rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-card rounded-lg border border-border p-2 flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveSection('backload')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'backload'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          <Package className="w-4 h-4 inline mr-1" />
          Backload Finder
        </button>
        <button
          onClick={() => setActiveSection('marketplace')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'marketplace'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          Partner Network
        </button>
        <button
          onClick={() => setActiveSection('offers')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'offers'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          My Offers ({offers.length})
        </button>
        <button
          onClick={() => setActiveSection('needs')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'needs'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          My Needs ({needs.length})
        </button>
        <button
          onClick={() => setActiveSection('matches')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'matches'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          Matches ({matches.length})
        </button>
        <button
          onClick={() => setActiveSection('config')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === 'config'
              ? 'bg-indigo-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-1" />
          Pricing & SLA
        </button>
      </div>

      {/* Backload Section */}
      {activeSection === 'backload' && (
        <ErrorBoundary fallback={
          <div className="p-6 bg-card rounded-lg border border-destructive/50">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-destructive" />
              <h3 className="text-lg font-semibold text-white">Backload Finder Error</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              The Backload Finder encountered an error. You can still use other sections of the Collaborate page.
            </p>
            <button
              onClick={() => setActiveSection('marketplace')}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
            >
              Go to Partner Network
            </button>
          </div>
        }>
          <BackloadSection />
        </ErrorBoundary>
      )}

      {/* Marketplace Section */}
      {activeSection === 'marketplace' && (
        <div className="space-y-6">
          {/* Hero Header */}
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-2xl border border-indigo-500/30 p-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8 text-indigo-400" />
                <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-indigo-200 bg-clip-text text-transparent">
                  Partner Network
                </h1>
              </div>
              <p className="text-foreground text-lg mb-6">Discover capacity opportunities and connect with logistics partners</p>
              
              {/* Stats Cards - Redesigned */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-success" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Offers</p>
                  </div>
              <p className="text-2xl font-bold text-white">{offers.filter(o => o.status === 'active').length}</p>
                  <p className="text-xs text-muted-foreground mt-1">Available capacity</p>
            </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-warning" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Needs</p>
                  </div>
              <p className="text-2xl font-bold text-white">{needs.filter(n => n.status === 'active').length}</p>
                  <p className="text-xs text-muted-foreground mt-1">Seeking capacity</p>
            </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Leaf className="w-4 h-4 text-success" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Carbon Saved</p>
                  </div>
              <p className="text-2xl font-bold text-success">{totalCarbonSavings.toFixed(2)} kg</p>
                  <p className="text-xs text-muted-foreground mt-1">CO₂ reduced</p>
            </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-indigo-400" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Value</p>
                  </div>
              <p className="text-2xl font-bold text-indigo-400">£{totalValue.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground mt-1">From matches</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by postcode, location, or date..."
                  value={marketplaceSearch}
                  onChange={(e) => setMarketplaceSearch(e.target.value)}
                  className="w-full bg-background/50 border border-border rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                />
              </div>
              
              {/* Filter Buttons */}
              <div className="flex gap-2">
              <button
                  onClick={() => setMarketplaceFilter('all')}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                    marketplaceFilter === 'all'
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/50'
                      : 'bg-input/50 text-foreground hover:bg-accent border border-border'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  All
              </button>
              <button
                  onClick={() => setMarketplaceFilter('offers')}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                    marketplaceFilter === 'offers'
                      ? 'bg-primary text-white shadow-lg shadow-success/50'
                      : 'bg-input/50 text-foreground hover:bg-accent border border-border'
                  }`}
                >
                  <Truck className="w-4 h-4" />
                  Offers
              </button>
              <button
                  onClick={() => setMarketplaceFilter('needs')}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                    marketplaceFilter === 'needs'
                      ? 'bg-warning text-white shadow-lg shadow-warning/50'
                      : 'bg-input/50 text-foreground hover:bg-accent border border-border'
                  }`}
                >
                  <Package className="w-4 h-4" />
                  Needs
              </button>
              </div>
            </div>
          </div>

          {/* Marketplace Cards Grid */}
          {(() => {
            const activeOffers = offers.filter(o => o.status === 'active');
            const activeNeeds = needs.filter(n => n.status === 'active');
            const filteredOffers = activeOffers.filter(offer => {
              if (!marketplaceSearch) return true;
              const searchLower = marketplaceSearch.toLowerCase();
              const primaryArea = offer.serviceAreas?.[0];
              const postcode = primaryArea?.postcode?.toLowerCase() || '';
              const address = primaryArea?.address?.toLowerCase() || '';
              const date = primaryArea?.date || '';
              return postcode.includes(searchLower) || address.includes(searchLower) || date.includes(searchLower);
            });
            const filteredNeeds = activeNeeds.filter(need => {
              if (!marketplaceSearch) return true;
              const searchLower = marketplaceSearch.toLowerCase();
              const deliveryPostcode = need.delivery.postcode?.toLowerCase() || '';
              const deliveryAddress = need.delivery.address?.toLowerCase() || '';
              const pickupPostcode = need.pickup.postcode?.toLowerCase() || '';
              const pickupAddress = need.pickup.address?.toLowerCase() || '';
              const date = need.delivery.date || '';
              return deliveryPostcode.includes(searchLower) || deliveryAddress.includes(searchLower) || 
                     pickupPostcode.includes(searchLower) || pickupAddress.includes(searchLower) || 
                     date.includes(searchLower);
            });
            
            const offersToShow = (marketplaceFilter === 'all' || marketplaceFilter === 'offers') ? filteredOffers : [];
            const needsToShow = (marketplaceFilter === 'all' || marketplaceFilter === 'needs') ? filteredNeeds : [];
            const totalItems = offersToShow.length + needsToShow.length;
            
            if (totalItems === 0) {
              return (
                <div className="text-center py-16 bg-card/30 rounded-2xl border border-border/50">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-indigo-400" />
                </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {marketplaceSearch ? 'No results found' : 'No listings found'}
                  </h3>
                  <p className="text-muted-foreground">
                    {marketplaceSearch 
                      ? `No ${marketplaceFilter === 'all' ? 'offers or needs' : marketplaceFilter === 'offers' ? 'offers' : 'needs'} match your search`
                      : `There are currently no active ${marketplaceFilter === 'all' ? 'offers or needs' : marketplaceFilter === 'offers' ? 'offers' : 'needs'} in the partner network`}
                  </p>
              </div>
              );
            }
            
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Filtered Offers */}
                {offersToShow.map((offer) => {
                  const approxPallets = Math.ceil(offer.availableKg / 500);
                  const pricePerPallet = offer.pricePerKg * 500;
                  const primaryServiceArea = offer.serviceAreas && offer.serviceAreas.length > 0 ? offer.serviceAreas[0] : null;
                  const formattedDate = primaryServiceArea?.date ? new Date(primaryServiceArea.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
                  
                  return (
                    <div
                      key={`offer-${offer.id}`}
                      onClick={() => {
                        if (!offer.id) {
                          setError(`Offer missing ID. VIN: ${offer.vehicleVin || 'unknown'}`);
                          logger.error('Offer missing ID when selecting', { offer });
                          return;
                        }
                        setSelectedOfferForMatching(offer);
                      }}
                      className="group relative bg-gradient-to-br from-success/10 via-success/5 to-transparent rounded-2xl border border-success/30 p-6 hover:border-success/50 hover:shadow-xl hover:shadow-success/20 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                    >
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-success/0 via-success/10 to-success/0 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300"></div>
                      
                      <div className="relative z-10">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-success to-success flex items-center justify-center shadow-lg shadow-success/30">
                              <Truck className="w-6 h-6 text-white" />
                            </div>
                <div>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-success/20 text-success border border-success/40">
                                Available Offer
                              </span>
                              {offer.serviceAreas && offer.serviceAreas.length > 1 && (
                                <p className="text-xs text-muted-foreground mt-1">{offer.serviceAreas.length} service areas</p>
                              )}
                </div>
              </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-success group-hover:translate-x-1 transition-all duration-300" />
                </div>

                        {/* Location */}
                        {primaryServiceArea && (
                          <div className="mb-4">
                            <div className="flex items-start gap-2 mb-2">
                              <MapPinned className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-white font-semibold text-lg">
                                  {primaryServiceArea.address || primaryServiceArea.postcode}
                                </p>
                                {primaryServiceArea.address && primaryServiceArea.postcode && (
                                  <p className="text-muted-foreground text-sm mt-0.5">{primaryServiceArea.postcode}</p>
                                )}
              </div>
                            </div>
                            {formattedDate !== '—' && (
                              <div className="flex items-center gap-2 text-muted-foreground ml-6">
                                <Clock className="w-4 h-4" />
                                <span className="text-sm">{formattedDate}</span>
                                {primaryServiceArea.startTime && primaryServiceArea.endTime && (
                                  <span className="text-sm">• {primaryServiceArea.startTime}-{primaryServiceArea.endTime}</span>
                                )}
                              </div>
                            )}
                            {offer.serviceAreas && offer.serviceAreas.length > 1 && (
                              <div className="mt-3 pt-3 border-t border-border">
                                <p className="text-xs text-muted-foreground mb-2">Additional service areas:</p>
                                <div className="space-y-2">
                                  {offer.serviceAreas.slice(1, 3).map((area, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                      <MapPinned className="w-3 h-3 text-success/60 flex-shrink-0" />
                                      <span className="text-foreground">{area.address || area.postcode}</span>
                                      {area.date && (
                                        <span className="text-muted-foreground text-xs">• {new Date(area.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                                      )}
                                    </div>
                                  ))}
                                  {offer.serviceAreas.length > 3 && (
                                    <p className="text-xs text-muted-foreground">+{offer.serviceAreas.length - 3} more area{offer.serviceAreas.length - 3 > 1 ? 's' : ''}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-background/50 rounded-lg p-3 border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Capacity</p>
                            <p className="text-white font-bold text-lg">{approxPallets}</p>
                            <p className="text-xs text-muted-foreground">pallets</p>
                          </div>
                          <div className="bg-background/50 rounded-lg p-3 border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Price</p>
                            <p className="text-white font-bold text-lg">£{pricePerPallet.toFixed(0)}</p>
                            <p className="text-xs text-muted-foreground">per pallet</p>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-success" />
                            <span className="text-xs text-muted-foreground">{offer.availableKg.toFixed(0)} kg available</span>
                          </div>
                          <span className="text-xs font-medium text-success group-hover:text-success">Take Offer →</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Filtered Needs */}
                {needsToShow.map((need) => {
                  const approxPallets = Math.ceil(need.weightKg / 500);
                  const maxPricePerPallet = need.maxPrice ? need.maxPrice / approxPallets : need.willingToPay ? need.willingToPay / approxPallets : null;
                  const formattedDate = need.delivery.date ? new Date(need.delivery.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
                  
                  return (
                    <div
                      key={`need-${need.id}`}
                      className="group relative bg-gradient-to-br from-warning/10 via-warning/5 to-transparent rounded-2xl border border-warning/30 p-6 hover:border-warning/50 hover:shadow-xl hover:shadow-warning/20 transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-warning/0 via-warning/10 to-warning/0 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300"></div>
                      
                      <div className="relative z-10">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-warning to-warning flex items-center justify-center shadow-lg shadow-warning/30">
                              <Package className="w-6 h-6 text-white" />
                            </div>
                <div>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-warning/20 text-warning border border-warning/40">
                                Seeking Capacity
                              </span>
                              {need.orderName && (
                                <p className="text-xs text-muted-foreground mt-1">{need.orderName}</p>
                              )}
                </div>
              </div>
            </div>

                        {/* Location */}
                        <div className="mb-4">
                          <div className="flex items-start gap-2 mb-2">
                            <MapPinned className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-white font-semibold text-lg">
                                {need.delivery.address || need.delivery.postcode || '—'}
                              </p>
                              {need.delivery.address && need.delivery.postcode && (
                                <p className="text-muted-foreground text-sm mt-0.5">{need.delivery.postcode}</p>
                              )}
          </div>
        </div>
                          {need.pickup.postcode || need.pickup.address ? (
                            <div className="ml-6 mb-2">
                              <p className="text-sm text-muted-foreground">
                                <span className="text-muted-foreground">From: </span>
                                {need.pickup.address || need.pickup.postcode}
                                {need.pickup.address && need.pickup.postcode && (
                                  <span className="text-muted-foreground"> ({need.pickup.postcode})</span>
                                )}
                              </p>
                            </div>
                          ) : null}
                          {formattedDate !== '—' && (
                            <div className="flex items-center gap-2 text-muted-foreground ml-6">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">{formattedDate}</span>
                              {need.delivery.time && (
                                <span className="text-sm">• {need.delivery.time}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-background/50 rounded-lg p-3 border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Required</p>
                            <p className="text-white font-bold text-lg">{approxPallets}</p>
                            <p className="text-xs text-muted-foreground">pallets</p>
                          </div>
                          <div className="bg-background/50 rounded-lg p-3 border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Budget</p>
                            {maxPricePerPallet ? (
                              <>
                                <p className="text-white font-bold text-lg">≤ £{maxPricePerPallet.toFixed(0)}</p>
                                <p className="text-xs text-muted-foreground">per pallet</p>
                              </>
                            ) : (
                              <p className="text-muted-foreground text-sm">—</p>
                            )}
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-warning" />
                            <span className="text-xs text-muted-foreground">{need.weightKg.toFixed(0)} kg needed</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}


      {/* My Offers Section */}
      {activeSection === 'offers' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">My Offers</h2>
              <p className="text-sm text-muted-foreground mt-1">Manage your created offers and view offer history</p>
            </div>
            <div className="flex gap-2">
            <button
                onClick={() => {
                  setSelectedOfferForMatching(null);
                  setShowCreateOffer(!showCreateOffer);
                }}
              className="bg-primary hover:bg-primary text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showCreateOffer ? 'Cancel' : 'Create Offer'}
            </button>
            </div>
          </div>

          {/* Create Offer from Planning */}
          {planningResult && planningResult.vehicles && !showCreateOffer && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-md font-semibold mb-4 text-white">Create Offers from Planning Result</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select vehicles from your planning result to create partner network offers. Only vehicles with available capacity are shown.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {planningResult.vehicles
                  .filter((v: any) => {
                    const assignedWeight = (v.assignedOrders || []).reduce((sum: number, o: any) => sum + (o.weightKg || 0), 0);
                    return (v.capacity_kg || 0) - assignedWeight > 0;
                  })
                  .map((vehicle: any) => {
                    const assignedWeight = (vehicle.assignedOrders || []).reduce((sum: number, o: any) => sum + (o.weightKg || 0), 0);
                    const availableKg = (vehicle.capacity_kg || 0) - assignedWeight;
                    return (
                      <div key={vehicle.vin} className="bg-background rounded-lg p-4 border border-border flex items-center justify-between">
                        <div>
                          <p className="text-white font-mono text-sm">{vehicle.vin}</p>
                          <p className="text-muted-foreground text-xs">
                            {availableKg.toFixed(0)} kg available • {vehicle.assignedOrders?.length || 0} orders assigned
                          </p>
                        </div>
                        <button
                          onClick={() => createOfferFromPlanning(vehicle)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-1 px-3 rounded-lg"
                        >
                          Create Offer
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Create Offer Form */}
          {showCreateOffer && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-md font-semibold mb-2 text-white">Create New Offer</h3>
              <p className="text-xs text-muted-foreground mb-4">Share your vehicle's available capacity with other operators. Adding location and date information helps create better matches.</p>
              
              {/* Vehicle & Capacity Section */}
              <div className="mb-6 pb-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Vehicle & Capacity
                </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Vehicle VIN *</label>
                  <select
                    value={newOffer.vehicleVin || ''}
                    onChange={(e) => handleVehicleSelect(e.target.value)}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select a vehicle...</option>
                    {loadingVehicles && <option disabled>Loading vehicles...</option>}
                    {fleetVehicles.map((vehicle) => (
                      <option key={vehicle.vin} value={vehicle.vin}>
                        {vehicle.vin} {vehicle.customerVehicleName ? `- ${vehicle.customerVehicleName}` : ''} {vehicle.type ? `(${vehicle.type})` : ''}
                      </option>
                    ))}
                  </select>
                  {fleetVehicles.length === 0 && !loadingVehicles && (
                    <p className="text-xs text-muted-foreground mt-1">No vehicles available. Check fleet API connection.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Vehicle Name</label>
                  <input
                    type="text"
                    value={newOffer.vehicleName || ''}
                    onChange={(e) => setNewOffer({ ...newOffer, vehicleName: e.target.value })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="Auto-populated from selection"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Available Capacity (kg) *</label>
                  <input
                    type="number"
                      min="0"
                    value={newOffer.availableKg || ''}
                    onChange={(e) => setNewOffer({ ...newOffer, availableKg: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="1000"
                  />
                    <p className="text-xs text-muted-foreground mt-1">How much weight capacity is available</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Available Volume (m³)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={newOffer.availableM3 || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, availableM3: Number(e.target.value) })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Optional"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Optional: volume capacity available</p>
                  </div>
                </div>
              </div>

              {/* Multiple Service Areas Section - Redesigned */}
              <div className="mb-6 pb-6 border-b border-border/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-white flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                        <MapPinned className="w-4 h-4 text-white" />
                      </div>
                      Service Areas
                    </h4>
                    <p className="text-xs text-muted-foreground ml-10">
                      Add multiple service areas where your vehicle will operate at different times
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setServiceAreas([...serviceAreas, {
                        id: `area-${Date.now()}`,
                        postcode: '',
                        address: '',
                        date: '',
                        startTime: '',
                        endTime: '',
                        type: 'both'
                      }]);
                    }}
                    className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all duration-200"
                  >
                    <Plus className="w-4 h-4" />
                    Add Service Area
                  </button>
                </div>
                
                {serviceAreas.length === 0 ? (
                  <div className="bg-background/50 rounded-xl p-8 border border-border/50 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/10 flex items-center justify-center">
                      <MapPinned className="w-8 h-8 text-indigo-400" />
                    </div>
                    <p className="text-muted-foreground mb-1">No service areas added</p>
                    <p className="text-sm text-muted-foreground">Click "Add Service Area" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {serviceAreas.map((area, index) => {
                      const areaId = area.id || `area-${index}`;
                      return (
                        <div 
                          key={areaId} 
                          className="group relative bg-gradient-to-br from-background/50 via-background/30 to-transparent rounded-xl p-5 border border-border/50 hover:border-indigo-500/30 transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
                                <span className="text-indigo-400 font-bold text-sm">{index + 1}</span>
                              </div>
                              <div>
                                <h5 className="text-sm font-semibold text-white">Service Area {index + 1}</h5>
                                {area.address && (
                                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <MapPinned className="w-3 h-3" />
                                    {area.address}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setServiceAreas(serviceAreas.filter((_, i) => i !== index));
                              }}
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                              title="Remove service area"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Form Fields */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {/* Location - Full Width on Mobile, 2 cols on Desktop */}
                            <div className="lg:col-span-2 relative">
                              <label className="block text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                                <MapPinned className="w-3 h-3 text-indigo-400" />
                                Location *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={area.address || area.postcode || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const updated = [...serviceAreas];
                                    updated[index] = { 
                                      ...updated[index], 
                                      address: value,
                                      postcode: value
                                    };
                                    setServiceAreas(updated);
                                    searchAddresses(value, areaId);
                                  }}
                                  onFocus={() => {
                                    if (area.address || area.postcode) {
                                      searchAddresses(area.address || area.postcode || '', areaId);
                                    }
                                  }}
                                  onBlur={() => {
                                    setTimeout(() => {
                                      setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
                                    }, 200);
                                  }}
                                  className="w-full bg-card/50 border border-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all placeholder:text-muted-foreground"
                                  placeholder="Enter address or postcode..."
                                />
                                {showSuggestions[areaId] && addressSuggestions[areaId] && addressSuggestions[areaId].length > 0 && (
                                  <div className="absolute z-20 w-full mt-2 bg-background border border-border rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                                    {addressSuggestions[areaId].map((suggestion, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => {
                                          const updated = [...serviceAreas];
                                          updated[index] = { 
                                            ...updated[index], 
                                            address: suggestion.address,
                                            postcode: suggestion.postcode
                                          };
                                          setServiceAreas(updated);
                                          setShowSuggestions(prev => ({ ...prev, [areaId]: false }));
                                          setAddressSuggestions(prev => ({ ...prev, [areaId]: [] }));
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-indigo-500/10 hover:text-white transition-colors border-b border-border last:border-b-0"
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                            <MapPinned className="w-4 h-4 text-indigo-400" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-white truncate">{suggestion.address}</p>
                                            <p className="text-xs text-muted-foreground">{suggestion.postcode}</p>
                                          </div>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {area.postcode && (
                                <p className="text-xs text-indigo-400 mt-1.5 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  {area.postcode}
                                </p>
                              )}
                            </div>

                            {/* Service Type */}
                            <div>
                              <label className="block text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                                <Package className="w-3 h-3 text-indigo-400" />
                                Service Type
                              </label>
                              <select
                                value={area.type}
                                onChange={(e) => {
                                  const updated = [...serviceAreas];
                                  updated[index] = { ...updated[index], type: e.target.value as 'pickup' | 'delivery' | 'both' };
                                  setServiceAreas(updated);
                                }}
                                className="w-full bg-card/50 border border-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                              >
                                <option value="both">Both Pickup & Delivery</option>
                                <option value="pickup">Pickup Only</option>
                                <option value="delivery">Delivery Only</option>
                              </select>
                            </div>

                            {/* Date */}
                            <div>
                              <label className="block text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-indigo-400" />
                                Date *
                              </label>
                              <input
                                type="date"
                                value={area.date}
                                onChange={(e) => {
                                  const updated = [...serviceAreas];
                                  updated[index] = { ...updated[index], date: e.target.value };
                                  setServiceAreas(updated);
                                }}
                                className="w-full bg-card/50 border border-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                              />
                            </div>

                            {/* Time Range - Combined */}
                            <div className="lg:col-span-2">
                              <label className="block text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                                <Clock className="w-3 h-3 text-indigo-400" />
                                Time Range *
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-muted-foreground mb-1">Start</label>
                                  <input
                                    type="time"
                                    value={area.startTime || ''}
                                    onChange={(e) => {
                                      const updated = [...serviceAreas];
                                      updated[index] = { ...updated[index], startTime: e.target.value };
                                      setServiceAreas(updated);
                                    }}
                                    className="w-full bg-card/50 border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-muted-foreground mb-1">End</label>
                                  <input
                                    type="time"
                                    value={area.endTime || ''}
                                    onChange={(e) => {
                                      const updated = [...serviceAreas];
                                      updated[index] = { ...updated[index], endTime: e.target.value };
                                      setServiceAreas(updated);
                                    }}
                                    className="w-full bg-card/50 border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pricing Section */}
              <div className="mb-6 pb-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Pricing
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Price per kg (£)</label>
                  <input
                    type="number"
                    step="0.01"
                      min="0"
                    value={newOffer.pricePerKg || config?.pricePerKg || 0.50}
                    onChange={(e) => setNewOffer({ ...newOffer, pricePerKg: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                    <p className="text-xs text-muted-foreground mt-1">Default: £{config?.pricePerKg || 0.50} per kg</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Price per km (£)</label>
                  <input
                    type="number"
                    step="0.01"
                      min="0"
                    value={newOffer.pricePerKm || config?.pricePerKm || 0.30}
                    onChange={(e) => setNewOffer({ ...newOffer, pricePerKm: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                    <p className="text-xs text-muted-foreground mt-1">Default: £{config?.pricePerKm || 0.30} per km</p>
                </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Operator Name</label>
                  <input
                      type="text"
                      value={newOffer.operatorName || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, operatorName: e.target.value })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Your name or company"
                  />
                </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                    <input
                      type="email"
                      value={newOffer.operatorEmail || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, operatorEmail: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="your@email.com"
                    />
                    <p className="text-xs text-muted-foreground mt-1">For receiving match notifications and agreements</p>
              </div>
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  onClick={createOffer}
                  className="bg-primary hover:bg-primary text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Create Offer
                </button>
                <button
                  onClick={() => {
                    setShowCreateOffer(false);
                    setNewOffer({});
                    setServiceAreas([]);
                  }}
                  className="bg-secondary hover:bg-accent text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Offers List */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-transparent">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Vehicle</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Available</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Service Area</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {offers.map((offer) => (
                  <tr key={offer.id} className="hover:bg-transparent transition">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        offer.status === 'active' 
                          ? 'bg-success/20 text-success border border-success/30'
                          : offer.status === 'matched'
                          ? 'bg-ring/20 text-ring border border-ring/30'
                          : 'bg-muted-foreground/20 text-muted-foreground border border-muted-foreground/30'
                      }`}>
                        {offer.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white font-mono text-xs">{offer.vehicleVin}</p>
                      <p className="text-muted-foreground text-xs">{offer.vehicleName}</p>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">
                      <p>{offer.availableKg.toFixed(0)} kg</p>
                      {offer.availableM3 && <p className="text-xs text-muted-foreground">{offer.availableM3.toFixed(2)} m³</p>}
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      {offer.serviceAreas && offer.serviceAreas.length > 0 ? (
                        <div className="space-y-1">
                          {offer.serviceAreas.slice(0, 2).map((area, idx) => (
                            <p key={idx}>
                              {area.postcode} ({area.type}) - {area.date} {area.startTime}-{area.endTime}
                            </p>
                          ))}
                          {offer.serviceAreas.length > 2 && (
                            <p className="text-muted-foreground">+{offer.serviceAreas.length - 2} more</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No service areas</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">
                      <p>£{offer.pricePerKg.toFixed(2)}/kg</p>
                      <p className="text-xs text-muted-foreground">£{offer.pricePerKm.toFixed(2)}/km</p>
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      {offer.serviceAreas && offer.serviceAreas.length > 0 ? (
                        <div className="space-y-1">
                          {offer.serviceAreas.map((area, idx) => (
                            <p key={idx}>{area.date} {area.startTime}-{area.endTime}</p>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      {offer.createdAt ? new Date(offer.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteOffer(offer.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete offer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {offers.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No offers created yet</p>
                <p className="text-sm text-muted-foreground">Click "Create Offer" to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Needs Section */}
      {activeSection === 'needs' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">My Needs</h2>
              <p className="text-sm text-muted-foreground mt-1">Manage your posted needs and view order history</p>
            </div>
            <button
              onClick={() => setShowCreateNeed(!showCreateNeed)}
              className="bg-ring hover:bg-ring text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showCreateNeed ? 'Cancel' : 'Post Need'}
            </button>
          </div>

          {/* Create Need Form */}
          {showCreateNeed && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-md font-semibold mb-2 text-white">Post New Need</h3>
              <p className="text-xs text-muted-foreground mb-4">Request capacity from other operators. Provide complete details to find the best matches.</p>
              
              {/* Order Details Section */}
              <div className="mb-6 pb-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Order Details
                </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Order ID
                      <span className="ml-2 text-xs text-muted-foreground">(Auto-generated)</span>
                    </label>
                    <div className="relative">
                  <input
                    type="text"
                        value={newNeed.orderId || autoGeneratedOrderId}
                        onChange={(e) => {
                          setNewNeed({ ...newNeed, orderId: e.target.value });
                          setAutoGeneratedOrderId(e.target.value);
                        }}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 pr-8 text-white text-sm font-mono"
                        placeholder="Auto-generated"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newId = generateOrderId();
                          setAutoGeneratedOrderId(newId);
                          setNewNeed({ ...newNeed, orderId: newId });
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-300 p-1"
                        title="Generate new ID"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Automatically generated, or customize if needed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Order Name</label>
                    <input
                      type="text"
                      value={newNeed.orderName || ''}
                      onChange={(e) => setNewNeed({ ...newNeed, orderName: e.target.value })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Optional description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Weight (kg) *</label>
                  <input
                    type="number"
                      min="0"
                    value={newNeed.weightKg || ''}
                    onChange={(e) => setNewNeed({ ...newNeed, weightKg: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="500"
                  />
                    <p className="text-xs text-muted-foreground mt-1">Required for matching</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Volume (m³)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={newNeed.volumeM3 || ''}
                      onChange={(e) => setNewNeed({ ...newNeed, volumeM3: Number(e.target.value) })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Optional"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Optional: helps with capacity matching</p>
                  </div>
                </div>
              </div>

              {/* Pickup Location Section */}
              <div className="mb-6 pb-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <MapPinned className="w-4 h-4" />
                  Pickup Location *
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 relative">
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Location *</label>
                    <div className="relative">
                  <input
                    type="text"
                        value={newNeed.pickup?.address || newNeed.pickup?.postcode || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewNeed({ 
                            ...newNeed, 
                            pickup: { 
                              ...newNeed.pickup, 
                              address: value,
                              postcode: value // Keep in sync for now
                            } 
                          });
                          searchAddresses(value, 'pickup');
                        }}
                        onFocus={() => {
                          if (newNeed.pickup?.address || newNeed.pickup?.postcode) {
                            searchAddresses(newNeed.pickup?.address || newNeed.pickup?.postcode || '', 'pickup');
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowSuggestions(prev => ({ ...prev, pickup: false }));
                          }, 200);
                        }}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Enter address or postcode (e.g., London, SW1A 1AA)"
                      />
                      {showSuggestions['pickup'] && addressSuggestions['pickup'] && addressSuggestions['pickup'].length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {addressSuggestions['pickup'].map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setNewNeed({ 
                                  ...newNeed, 
                                  pickup: { 
                                    ...newNeed.pickup,
                                    address: suggestion.address,
                                    postcode: suggestion.postcode
                                  } 
                                });
                                setShowSuggestions(prev => ({ ...prev, pickup: false }));
                                setAddressSuggestions(prev => ({ ...prev, pickup: [] }));
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent hover:text-white transition-colors border-b border-border last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <MapPinned className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                <div>
                                  <p className="font-medium">{suggestion.address}</p>
                                  <p className="text-xs text-muted-foreground">{suggestion.postcode}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {newNeed.pickup?.postcode && (
                      <p className="text-xs text-muted-foreground mt-1">Postcode: {newNeed.pickup.postcode}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Required for location matching</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Date</label>
                    <input
                      type="date"
                      value={newNeed.pickup?.date || ''}
                    onChange={(e) => setNewNeed({ 
                      ...newNeed, 
                        pickup: { ...newNeed.pickup, date: e.target.value } 
                    })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Time</label>
                  <input
                      type="time"
                      value={newNeed.pickup?.time || ''}
                    onChange={(e) => setNewNeed({ 
                      ...newNeed, 
                        pickup: { ...newNeed.pickup, time: e.target.value } 
                    })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                </div>
              </div>

              {/* Delivery Location Section */}
              <div className="mb-6 pb-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <MapPinned className="w-4 h-4" />
                  Delivery Location *
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 relative">
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Location *</label>
                    <div className="relative">
                  <input
                    type="text"
                        value={newNeed.delivery?.address || newNeed.delivery?.postcode || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewNeed({ 
                            ...newNeed, 
                            delivery: { 
                              ...newNeed.delivery, 
                              address: value,
                              postcode: value // Keep in sync for now
                            } 
                          });
                          searchAddresses(value, 'delivery');
                        }}
                        onFocus={() => {
                          if (newNeed.delivery?.address || newNeed.delivery?.postcode) {
                            searchAddresses(newNeed.delivery?.address || newNeed.delivery?.postcode || '', 'delivery');
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowSuggestions(prev => ({ ...prev, delivery: false }));
                          }, 200);
                        }}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Enter address or postcode (e.g., Manchester, M1 1AA)"
                      />
                      {showSuggestions['delivery'] && addressSuggestions['delivery'] && addressSuggestions['delivery'].length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {addressSuggestions['delivery'].map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setNewNeed({ 
                                  ...newNeed, 
                                  delivery: { 
                                    ...newNeed.delivery,
                                    address: suggestion.address,
                                    postcode: suggestion.postcode
                                  } 
                                });
                                setShowSuggestions(prev => ({ ...prev, delivery: false }));
                                setAddressSuggestions(prev => ({ ...prev, delivery: [] }));
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent hover:text-white transition-colors border-b border-border last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <MapPinned className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                <div>
                                  <p className="font-medium">{suggestion.address}</p>
                                  <p className="text-xs text-muted-foreground">{suggestion.postcode}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {newNeed.delivery?.postcode && (
                      <p className="text-xs text-muted-foreground mt-1">Postcode: {newNeed.delivery.postcode}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Required for location matching</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Date</label>
                    <input
                      type="date"
                      value={newNeed.delivery?.date || newNeed.deliveryDate || ''}
                    onChange={(e) => setNewNeed({ 
                      ...newNeed, 
                        delivery: { ...newNeed.delivery, date: e.target.value },
                        deliveryDate: e.target.value
                    })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Time</label>
                  <input
                      type="time"
                      value={newNeed.delivery?.time || ''}
                    onChange={(e) => setNewNeed({ 
                      ...newNeed, 
                        delivery: { ...newNeed.delivery, time: e.target.value } 
                    })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Budget
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Willing to Pay (£)</label>
                  <input
                    type="number"
                    step="0.01"
                      min="0"
                    value={newNeed.willingToPay || ''}
                    onChange={(e) => setNewNeed({ ...newNeed, willingToPay: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="50.00"
                  />
                    <p className="text-xs text-muted-foreground mt-1">Preferred price</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Maximum Price (£)</label>
                  <input
                    type="number"
                    step="0.01"
                      min="0"
                    value={newNeed.maxPrice || ''}
                    onChange={(e) => setNewNeed({ ...newNeed, maxPrice: Number(e.target.value) })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="100.00"
                  />
                    <p className="text-xs text-muted-foreground mt-1">Hard limit - matches above this will be rejected</p>
                </div>
              </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  onClick={createNeed}
                  className="bg-ring hover:bg-ring text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Post Need
                </button>
                <button
                  onClick={() => {
                    setShowCreateNeed(false);
                    setNewNeed({});
                    setAutoGeneratedOrderId('');
                  }}
                  className="bg-secondary hover:bg-accent text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Needs List */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-transparent">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Order</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Pickup</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Delivery</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Budget</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {needs.map((need) => (
                  <tr key={need.id} className="hover:bg-transparent transition">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        need.status === 'active' 
                          ? 'bg-warning/20 text-warning border border-warning/30'
                          : need.status === 'matched'
                          ? 'bg-ring/20 text-ring border border-ring/30'
                          : 'bg-muted-foreground/20 text-muted-foreground border border-muted-foreground/30'
                      }`}>
                        {need.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white font-mono text-xs">{need.orderId}</p>
                      <p className="text-muted-foreground text-xs">{need.orderName}</p>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">
                      <p>{need.weightKg.toFixed(0)} kg</p>
                      {need.volumeM3 && <p className="text-xs text-muted-foreground">{need.volumeM3.toFixed(2)} m³</p>}
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      <p>{need.pickup.postcode || need.pickup.address || '—'}</p>
                      {need.pickup.date && (
                        <p className="text-muted-foreground">{need.pickup.date}{need.pickup.time ? ` ${need.pickup.time}` : ''}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      <p>{need.delivery.postcode || need.delivery.address || '—'}</p>
                      {need.delivery.date && (
                        <p className="text-muted-foreground">{need.delivery.date}{need.delivery.time ? ` ${need.delivery.time}` : ''}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">
                      {need.maxPrice ? (
                        <p className="font-semibold">£{need.maxPrice.toFixed(2)}</p>
                      ) : need.willingToPay ? (
                        <p>£{need.willingToPay.toFixed(2)}</p>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">
                      {need.createdAt ? new Date(need.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteNeed(need.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete need"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {needs.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No needs posted yet</p>
                <p className="text-sm text-muted-foreground">Click "Post Need" to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Matches Section */}
      {activeSection === 'matches' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Matches</h2>
            <button
              onClick={runMatching}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Find Matches
            </button>
          </div>

          {/* Matching Tips */}
          {matches.length === 0 && (
            <div className="bg-ring/10 border border-ring/40 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-ring mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Tips for Better Matches
              </h3>
              <ul className="text-xs text-ring space-y-1 list-disc list-inside">
                <li>Ensure offers and needs have location information (postcodes)</li>
                <li>Add date/time information to both offers and needs</li>
                <li>Set realistic price expectations in needs (max price)</li>
                <li>Provide volume information when available</li>
                <li>Check that capacity (weight/volume) is compatible</li>
              </ul>
            </div>
          )}

          <div className="space-y-4">
            {matches.map((match) => {
              const offer = offers.find(o => o.id === match.offerId);
              const need = needs.find(n => n.id === match.needId);
              const isAccepted = match.status === 'accepted' || match.status === 'auto-matched';
              
              // Calculate compatibility indicators
              const capacityMatch = offer && need ? offer.availableKg >= need.weightKg : false;
              const volumeMatch = offer && need && offer.availableM3 && need.volumeM3 
                ? offer.availableM3 >= need.volumeM3 
                : true; // Assume match if volume not specified
              const priceMatch = need && need.maxPrice ? match.price <= need.maxPrice : true;
              const distanceMatch = match.pickupDistance && match.deliveryDistance 
                ? (match.pickupDistance <= (config?.maxDistanceKm || 50)) && (match.deliveryDistance <= (config?.maxDistanceKm || 50))
                : true;
              const matchScore = capacityMatch && volumeMatch && priceMatch && distanceMatch;

              return (
                <div
                  key={match.id}
                  className={`bg-card rounded-lg border ${
                    isAccepted ? 'border-success' : matchScore ? 'border-ring' : 'border-border'
                  } p-6`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">
                          {offer?.vehicleVin} ↔ {need?.orderId}
                        </h3>
                        {isAccepted && (
                          <span className="inline-flex items-center gap-1 text-xs bg-success/20 text-success px-2 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {match.status === 'auto-matched' ? 'Auto-Matched' : 'Accepted'}
                          </span>
                        )}
                        {!isAccepted && matchScore && (
                          <span className="inline-flex items-center gap-1 text-xs bg-ring/20 text-ring px-2 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Compatible Match
                          </span>
                        )}
                      </div>
                      
                      {/* Compatibility Indicators */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {capacityMatch && (
                          <span className="inline-flex items-center gap-1 text-xs bg-success/15 text-success px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3" />
                            Capacity OK
                          </span>
                        )}
                        {volumeMatch && need?.volumeM3 && (
                          <span className="inline-flex items-center gap-1 text-xs bg-success/15 text-success px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3" />
                            Volume OK
                          </span>
                        )}
                        {priceMatch && (
                          <span className="inline-flex items-center gap-1 text-xs bg-success/15 text-success px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3" />
                            Within Budget
                          </span>
                        )}
                        {distanceMatch && (
                          <span className="inline-flex items-center gap-1 text-xs bg-success/15 text-success px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3" />
                            Distance OK
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Price</p>
                          <p className="text-white font-semibold">£{match.price.toFixed(2)}</p>
                          {need?.maxPrice && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {match.price <= need.maxPrice ? '✓ Within budget' : '⚠ Over budget'}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Carbon Savings</p>
                          <p className="text-success font-semibold">{match.carbonSavings.toFixed(3)} kg</p>
                          <p className="text-xs text-success mt-1">
                            {match.baselineEmissions > 0 
                              ? `${((match.carbonSavings / match.baselineEmissions) * 100).toFixed(1)}% reduction`
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Pickup Distance</p>
                          <p className="text-foreground">{match.pickupDistance?.toFixed(2) || 'N/A'} km</p>
                          {match.pickupDistance && config?.maxDistanceKm && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {match.pickupDistance <= config.maxDistanceKm ? '✓ Within range' : '⚠ Too far'}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Delivery Distance</p>
                          <p className="text-foreground">{match.deliveryDistance?.toFixed(2) || 'N/A'} km</p>
                          {match.deliveryDistance && config?.maxDistanceKm && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {match.deliveryDistance <= config.maxDistanceKm ? '✓ Within range' : '⚠ Too far'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!isAccepted && (
                        <button
                          onClick={() => acceptMatch(match.id)}
                          className="bg-primary hover:bg-primary text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                        >
                          Accept Match
                        </button>
                      )}
                      <button
                        onClick={() => downloadReceipt(match)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center gap-2"
                        title="Download receipt"
                      >
                        <Download className="w-4 h-4" />
                        Receipt
                      </button>
                    </div>
                  </div>

                  {/* Carbon Details */}
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Leaf className="w-4 h-4 text-success" />
                      Carbon Impact Analysis
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Baseline Emissions</p>
                        <p className="text-foreground">{match.baselineEmissions.toFixed(3)} kg CO₂</p>
                        <p className="text-xs text-muted-foreground mt-1">Standalone delivery</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">With Collaboration</p>
                        <p className="text-foreground">{match.offerEmissions.toFixed(3)} kg CO₂</p>
                        <p className="text-xs text-muted-foreground mt-1">Using shared vehicle</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Savings</p>
                        <p className="text-success font-semibold">{match.carbonSavings.toFixed(3)} kg CO₂</p>
                        <p className="text-xs text-success mt-1">
                          {match.baselineEmissions > 0 
                            ? `${((match.carbonSavings / match.baselineEmissions) * 100).toFixed(1)}% reduction`
                            : '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Offer & Need Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-background rounded-lg p-4 border border-border">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Offer Details
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Vehicle: </span>
                          <span className="text-white font-mono">{offer?.vehicleVin}</span>
                          {offer?.vehicleName && <span className="text-muted-foreground ml-1">({offer.vehicleName})</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Available Capacity: </span>
                          <span className="text-white">{offer?.availableKg.toFixed(0)} kg</span>
                          {offer?.availableM3 && <span className="text-muted-foreground ml-1">/ {offer.availableM3.toFixed(2)} m³</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Pricing: </span>
                          <span className="text-white">£{offer?.pricePerKg.toFixed(2)}/kg</span>
                          <span className="text-muted-foreground">, £{offer?.pricePerKm.toFixed(2)}/km</span>
                        </div>
                        {offer?.pickupArea?.postcode && (
                          <div>
                            <span className="text-muted-foreground">Pickup Area: </span>
                            <span className="text-white">{offer.pickupArea.postcode}</span>
                          </div>
                        )}
                        {offer?.deliveryArea?.postcode && (
                          <div>
                            <span className="text-muted-foreground">Delivery Area: </span>
                            <span className="text-white">{offer.deliveryArea.postcode}</span>
                          </div>
                        )}
                        {offer?.pickupDate && (
                          <div>
                            <span className="text-muted-foreground">Pickup: </span>
                            <span className="text-white">{offer.pickupDate}</span>
                            {offer.pickupTime && <span className="text-muted-foreground ml-1">at {offer.pickupTime}</span>}
                          </div>
                        )}
                        {offer?.deliveryDate && (
                          <div>
                            <span className="text-muted-foreground">Delivery: </span>
                            <span className="text-white">{offer.deliveryDate}</span>
                            {offer.deliveryTime && <span className="text-muted-foreground ml-1">at {offer.deliveryTime}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="bg-background rounded-lg p-4 border border-border">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Need Details
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Order: </span>
                          <span className="text-white font-mono">{need?.orderId}</span>
                          {need?.orderName && <span className="text-muted-foreground ml-1">({need.orderName})</span>}
                    </div>
                        <div>
                          <span className="text-muted-foreground">Weight: </span>
                          <span className="text-white">{need?.weightKg.toFixed(0)} kg</span>
                          {need?.volumeM3 && <span className="text-muted-foreground ml-1">/ {need.volumeM3.toFixed(2)} m³</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Pickup: </span>
                          <span className="text-white">{need?.pickup.postcode || need?.pickup.address || '—'}</span>
                          {need?.pickup.date && <span className="text-muted-foreground ml-1">on {need.pickup.date}</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Delivery: </span>
                          <span className="text-white">{need?.delivery.postcode || need?.delivery.address || '—'}</span>
                          {need?.delivery.date && <span className="text-muted-foreground ml-1">on {need.delivery.date}</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Budget: </span>
                          {need?.maxPrice ? (
                            <span className="text-white">£{need.maxPrice.toFixed(2)} max</span>
                          ) : need?.willingToPay ? (
                            <span className="text-white">£{need.willingToPay.toFixed(2)} preferred</span>
                          ) : (
                            <span className="text-muted-foreground">Not specified</span>
                          )}
                        </div>
                        {capacityMatch && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <span className="text-success">✓ Capacity compatible</span>
                            <span className="text-muted-foreground ml-2">
                              ({need?.weightKg.toFixed(0)} kg of {offer?.availableKg.toFixed(0)} kg available)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {matches.length === 0 && (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <p className="text-muted-foreground mb-4">No matches found</p>
                <button
                  onClick={runMatching}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Find Matches
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Config Section */}
      {activeSection === 'config' && config && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-white">Pricing & SLA Configuration</h2>
          
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-md font-semibold mb-4 text-white">Pricing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Price per kg (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={configForm.pricePerKg || 0}
                  onChange={(e) => setConfigForm({ ...configForm, pricePerKg: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Base price per kilogram of cargo</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Price per km (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={configForm.pricePerKm || 0}
                  onChange={(e) => setConfigForm({ ...configForm, pricePerKm: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Additional price per kilometer</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Minimum Order Value (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={configForm.minOrderValue || 0}
                  onChange={(e) => setConfigForm({ ...configForm, minOrderValue: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum value for partner network orders</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-md font-semibold mb-4 text-white">Matching Rules</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Matching Radius (km)</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={configForm.matchingRadiusKm || 5}
                  onChange={(e) => setConfigForm({ ...configForm, matchingRadiusKm: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Radius for automatic matching (default: 5km)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Maximum Distance (km)</label>
                <input
                  type="number"
                  min="0"
                  value={configForm.maxDistanceKm || 0}
                  onChange={(e) => setConfigForm({ ...configForm, maxDistanceKm: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum distance from route for matching</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">SLA (hours)</label>
                <input
                  type="number"
                  value={configForm.slaHours || 0}
                  onChange={(e) => setConfigForm({ ...configForm, slaHours: Number(e.target.value) })}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Service level agreement in hours</p>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configForm.autoMatchEnabled ?? true}
                    onChange={(e) => setConfigForm({ ...configForm, autoMatchEnabled: e.target.checked })}
                    className="accent-indigo-600"
                  />
                  Enable automatic matching
                </label>
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  When enabled, the system automatically creates matches for the best offers and needs
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-md font-semibold mb-4 text-white">Terms & Conditions</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Standard Terms</label>
              <textarea
                value={configForm.terms || ''}
                onChange={(e) => setConfigForm({ ...configForm, terms: e.target.value })}
                rows={4}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                placeholder="Standard partner network terms apply..."
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={updateConfig}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Save Configuration
            </button>
            <button
              onClick={() => {
                setConfigForm(config || {});
              }}
              className="bg-secondary hover:bg-accent text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Manual Matching Modal - Global (accessible from all sections) */}
      {selectedOfferForMatching && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-2xl w-full max-h-screen-90 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Take Offer: {selectedOfferForMatching.vehicleVin}</h3>
              <button
                onClick={() => {
                  setSelectedOfferForMatching(null);
                  setManualMatchDetails({});
                }}
                className="text-muted-foreground hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Offer Summary */}
            <div className="bg-background rounded-lg p-4 mb-4 border border-border">
              <h4 className="text-sm font-semibold text-white mb-2">Offer Details</h4>
              <div className="grid grid-cols-2 gap-2 text-xs text-foreground">
                <div>Available: {selectedOfferForMatching.availableKg.toFixed(0)} kg</div>
                <div>Price: £{selectedOfferForMatching.pricePerKg.toFixed(2)}/kg, £{selectedOfferForMatching.pricePerKm.toFixed(2)}/km</div>
                {selectedOfferForMatching.serviceAreas && selectedOfferForMatching.serviceAreas.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground mb-1">Service Areas:</p>
                    {selectedOfferForMatching.serviceAreas.map((area, idx) => (
                      <p key={idx} className="text-foreground">
                        {area.postcode} - {area.type} - {area.date} {area.startTime}-{area.endTime}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Manual Match Form */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Enter Your Pallet Details</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Weight (kg) *</label>
                    <input
                      type="number"
                      min="0"
                      value={manualMatchDetails.weightKg || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, weightKg: Number(e.target.value) })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Volume (m³)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={manualMatchDetails.volumeM3 || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, volumeM3: Number(e.target.value) })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                    <input
                      type="text"
                      value={manualMatchDetails.description || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, description: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Brief description of your pallet"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Postcode *</label>
                    <input
                      type="text"
                      value={manualMatchDetails.pickupPostcode || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, pickupPostcode: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="SW1A 1AA"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Address</label>
                    <input
                      type="text"
                      value={manualMatchDetails.pickupAddress || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, pickupAddress: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Date</label>
                    <input
                      type="date"
                      value={manualMatchDetails.pickupDate || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, pickupDate: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Pickup Time</label>
                    <input
                      type="time"
                      value={manualMatchDetails.pickupTime || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, pickupTime: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Postcode *</label>
                    <input
                      type="text"
                      value={manualMatchDetails.deliveryPostcode || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, deliveryPostcode: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="M1 1AA"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Address</label>
                    <input
                      type="text"
                      value={manualMatchDetails.deliveryAddress || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, deliveryAddress: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="456 High St"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Date</label>
                    <input
                      type="date"
                      value={manualMatchDetails.deliveryDate || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, deliveryDate: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Delivery Time</label>
                    <input
                      type="time"
                      value={manualMatchDetails.deliveryTime || ''}
                      onChange={(e) => setManualMatchDetails({ ...manualMatchDetails, deliveryTime: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-border">
                <button
                  onClick={takeOffer}
                  className="bg-primary hover:bg-primary text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex-1"
                >
                  Confirm & Generate Agreement
                </button>
                <button
                  onClick={() => {
                    setSelectedOfferForMatching(null);
                    setManualMatchDetails({});
                  }}
                  className="bg-secondary hover:bg-accent text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaborate;

